import * as Equivalence from 'effect/Equivalence';
import {isFunction} from 'effect/Function';
import React, {useCallback, useContext, useMemo, useState} from 'react';
import {useBoolean} from '../../Hooks';
import {asREM, cn} from '../../Utilities';
import {joinCallbacks} from '../../Utilities/joinCallbacks';
import ResizingInput from '../ResizingInput/ResizingInput';
import {RsnTextButton} from '../RsnTextButton/RsnTextButton';
import {RsnSkeleton} from '../SkeletalElements/Skeleton';
import scss from './RsnAmountInput.module.scss';
import {RsnAmount, toBigNumber, useAmountFormatter, useAmountInputControls} from './RsnAmountInput.utils';

export interface RsnAmountInputProps {
    suffix: string;
    maxIntegers: number;
    maxDecimals: number;
    minDecimals?: number;
    issue?: string;
    isInvalid?: boolean;
    helperText?: string;
    hintAction?: React.ReactNode;

    onChange: (amount: RsnAmount) => void;
    value: RsnAmount;

    onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event?: React.FocusEvent<HTMLInputElement>) => void;
}

function RsnAmountInput({
    suffix,
    issue,
    isInvalid,
    helperText,
    hintAction,

    value,
    onChange,
    onBlur,
    onFocus,

    maxIntegers,
    maxDecimals,
    minDecimals,
}: RsnAmountInputProps) {
    if (issue) {
        helperText = issue;
        isInvalid = true;
    }

    const formatter = useAmountFormatter({maxIntegers, maxDecimals, minDecimals});

    // TODO: we need to try approach form onboarding/lib/forms/formatting.ts
    const controls = useAmountInputControls(formatter, value, onChange);

    const [shaking, setShaking] = useBoolean();
    return (
        <div
            className={cn(scss.root, {
                [scss.invalid]: isInvalid,
            })}
        >
            <label className={scss.inputWrapper}>
                <ResizingInput
                    {...controls}
                    onChange={ev => controls.onChange(ev.target.value)}
                    onBlur={joinCallbacks(onBlur, controls.onBlur)}
                    onFocus={onFocus}
                    onInvalid={setShaking.on}
                    onAnimationEnd={setShaking.off}
                    className={`${scss.input} ${shaking ? scss.shaking : ''}`}
                    placeholder='0'
                    min='0'
                    autoComplete='off'
                    type='text'
                    inputMode='decimal'
                />
                <span className={scss.inputSuffix}>{suffix}</span>
            </label>
            {!!helperText && (
                <div className={scss.hintBlock}>
                    <span className={scss.hintMessage}>{helperText}</span>
                    <InputControlsProvider value={controls}>{hintAction}</InputControlsProvider>
                </div>
            )}
        </div>
    );
}

const [InputControlsProvider, useInputControls] = (context => [context.Provider, () => useContext(context)])(
    React.createContext<{value: string; onChange: (rawEnteredValue: string) => void} | null>(null)
);

const HintActionUseAll = ({
    all,
    onClick,
    used = Equivalence.string,
    text,
}: {
    all: string;
    used?: boolean | Equivalence.Equivalence<string>;
    onClick?: (value: string) => void;
    text?: string;
}) => {
    const {value, onChange} = useInputControls()!;
    const handleClick = () => {
        if (onClick) {
            onClick(all.toString());
        } else {
            onChange(all.toString());
        }
    };

    const defaultText = 'Use all funds';
    if (text === undefined) {
        text = defaultText;
    }
    const usingAll = isFunction(used) ? used(value, all) : used;
    if (usingAll) {
        text = 'All funds are used';
    }

    return (
        <RsnTextButton.Inline
            type={'button'}
            disabled={usingAll}
            underlined={!usingAll}
            onClick={handleClick}
            size={'l'}
        >
            {text}
        </RsnTextButton.Inline>
    );
};

RsnAmountInput.HintActionUseAll = HintActionUseAll;
RsnAmountInput.Skeleton = function RsnAmountInputSkeleton() {
    return (
        <div>
            <div className='grid place-items-center border-b border-solid border-neutral-30 py-4'>
                <RsnSkeleton height={asREM(24)} width={asREM(64)} />
            </div>
            <div className='flex items-center justify-between py-2'>
                <RsnSkeleton height={asREM(12)} width={asREM(140)} />
                <RsnSkeleton height={asREM(12)} width={asREM(64)} />
            </div>
        </div>
    );
};
RsnAmountInput.toBigNumber = toBigNumber;

type CompatProps = Omit<RsnAmountInputProps, 'onChange' | 'value'> & {
    onChange: (string: string) => void;
    value: string;
};
type UncontrolledProps = Omit<RsnAmountInputProps, 'onChange' | 'value'> & {
    onChange: (string: string) => void;
};

RsnAmountInput.Uncontrolled = function Uncontrolled({onChange: externalOnChange, ...props}: UncontrolledProps) {
    const [value, setState] = useState(RsnAmount.empty);
    const onChange = useCallback(
        (value: RsnAmount) => {
            setState(value);
            externalOnChange(RsnAmount.toString(value));
        },
        [externalOnChange]
    );
    return RsnAmountInput({...props, value, onChange});
};

RsnAmountInput.Compat = function Compat({value, onChange, ...props}: CompatProps) {
    const handleChange = (value: RsnAmount) => {
        onChange(RsnAmount.toString(value));
    };
    const amount = useMemo(() => RsnAmount.fromString(value), [value]);
    return <RsnAmountInput {...props} value={amount} onChange={handleChange} />;
};

export default RsnAmountInput;
